import {
  ConditionalFormattingState,
  DiffType,
  GetCssClassNamesOption,
  RepresentationData,
} from '@ardoq/data-model';
import { GroupType } from '@ardoq/api-types';
import {
  GraphItem,
  getGraphItemMetadata,
  GraphItemMetadata,
} from './GraphItem';
import SparkMD5 from 'spark-md5';
import {
  GraphItemFormattingMaps,
  ItemLabels,
  RawGraphNode2,
  SVGColorAttributes,
  URLFieldValue,
} from '../types';
import {
  countDescendantComponents,
  getGroupTypeCss,
  getWorkspaceCssClassNames,
  getWorkspaceGroupCss,
} from './utils';
import { ComponentGraphGroupModel } from './types';
import { classes } from '@ardoq/common-helpers';
import {
  DEFAULT_EMPTY_ITEM_LABELS,
  LI_BACKGROUND_CLASS_NAME,
} from '@ardoq/global-consts';
import { GraphInterface } from '../graphInterface/graphInterface';
import { RepresentationInterface } from '../representationInterface/representationInterface';
import { getComponentGraphNodeMetadata } from './ComponentGraphNode';
import { logError } from '@ardoq/logging';

type GraphGroupMetadata = GraphItemMetadata & {
  representationData: RepresentationData | null;
  subLabels?: string[];
  type: GroupType;
  isContext: boolean;
  visualDiffType: DiffType;
  itemLabels: ItemLabels;
  urlFieldValues: URLFieldValue[] | null;
  cssClassNames: string;
  collapsed: boolean;
  isCollapsible: boolean;
  color: SVGColorAttributes;
  conditionalFormattingState: ConditionalFormattingState;
};

type GetMetadataArgsBase = {
  viewInterface: GraphInterface & RepresentationInterface;
  rawGraphItem: RawGraphNode2;
  nodeLabelsData: Map<string, ItemLabels>;
  isCollapsed: boolean;
  isContext: boolean;
  isCollapsible: boolean;
};
type NonComponentGroupMetadataArgs = GetMetadataArgsBase &
  GraphItemFormattingMaps & {
    type: GroupType;
    modelId?: string;
  };
const getNonComponentGroupMetadata = ({
  viewInterface,
  rawGraphItem,
  nodeLabelsData,
  isCollapsed,
  isCollapsible,
  type,
  modelId,
  nodeColorsByGraphItemId,
  conditionalFormattingState,
  isContext,
}: NonComponentGroupMetadataArgs) => {
  const graphItemMetadata = getGraphItemMetadata({
    viewInterface,
    rawGraphItem,
  });

  const cssClassNames =
    (modelId && type === GroupType.WORKSPACE
      ? getWorkspaceGroupCss(getWorkspaceCssClassNames(viewInterface, modelId))
      : getGroupTypeCss(type)) ?? '';

  const itemLabels =
    nodeLabelsData.get(rawGraphItem.id) ?? DEFAULT_EMPTY_ITEM_LABELS;

  return {
    ...graphItemMetadata,
    type,
    collapsed: isCollapsed,
    isCollapsible,
    urlFieldValues: null,
    itemLabels,
    cssClassNames,
    visualDiffType: DiffType.NONE,
    representationData: null,
    subLabels: rawGraphItem.subLabels,
    color: nodeColorsByGraphItemId.get(rawGraphItem.id) ?? {},
    conditionalFormattingState:
      conditionalFormattingState.get(rawGraphItem.id) ??
      ConditionalFormattingState.NORMAL,
    isContext,
  };
};

type GetComponentGraphGroupMetadataArgs = GetMetadataArgsBase &
  GraphItemFormattingMaps & {
    urlFieldValuesByComponentId: Map<string, URLFieldValue[] | null>;
    representationDataByComponentId: Map<string, RepresentationData | null>;
    isContext: boolean;
  };
export const getComponentGraphGroupMetadata = ({
  viewInterface,
  rawGraphItem,
  urlFieldValuesByComponentId,
  representationDataByComponentId,
  nodeColorsByGraphItemId,
  conditionalFormattingState,
  nodeLabelsData,
  isCollapsed,
  isCollapsible,
  isContext,
}: GetComponentGraphGroupMetadataArgs): GraphGroupMetadata | null => {
  const isComponent = Boolean(
    rawGraphItem.modelId && viewInterface.isComponent(rawGraphItem.modelId)
  );

  const { type, modelId } = rawGraphItem;

  if (!type) {
    logError(new Error('Constructing a Group Node without a valid group type'));
    return null;
  }

  if (isComponent) {
    const nodeMetadata = getComponentGraphNodeMetadata({
      viewInterface,
      rawGraphItem,
      urlFieldValuesByComponentId,
      representationDataByComponentId,
      nodeColorsByGraphItemId,
      conditionalFormattingState,
      nodeLabelsData,
      isContext,
    });

    return nodeMetadata
      ? {
          ...nodeMetadata,
          collapsed: isCollapsed,
          isCollapsible,
          type,
          subLabels: rawGraphItem.subLabels,
          isContext: nodeMetadata.isContext,
        }
      : null;
  }

  return getNonComponentGroupMetadata({
    viewInterface,
    rawGraphItem,
    nodeLabelsData,
    isCollapsed,
    isCollapsible,
    type,
    modelId,
    nodeColorsByGraphItemId,
    conditionalFormattingState,
    isContext,
  });
};

type ConstructorArgs = {
  rawGraphItem: RawGraphNode2;
  metadata: GraphGroupMetadata;
};

export class ComponentGraphGroup
  extends GraphItem
  implements ComponentGraphGroupModel
{
  static create(rawGraphItem: RawGraphNode2, metadata: GraphGroupMetadata) {
    if (!metadata.type) {
      throw Error('Invalid group.');
    }
    return new ComponentGraphGroup({ rawGraphItem, metadata });
  }

  public collapsed: boolean;
  public isCollapsible: boolean;
  public readonly descendantCount;
  public readonly subLabel?: string;
  public readonly type: GroupType;
  protected metadata: GraphGroupMetadata;

  protected constructor({
    rawGraphItem: { modelId, id, children },
    metadata,
  }: ConstructorArgs) {
    super(modelId ?? '', metadata, id);

    this.metadata = metadata;

    const { collapsed, isCollapsible, type, subLabels } = this.metadata;
    this.collapsed = collapsed;
    this.isCollapsible = isCollapsible;
    this.descendantCount = children ? countDescendantComponents(children) : 0;
    this.type = type;
    this.subLabel = subLabels ? subLabels.join(', ') : '';

    this.hash = SparkMD5.hash(this.getRawHash());
  }

  override getLabel() {
    // legacy compatibility
    return this.label || this.metadata.itemLabels.mainLabel || '';
  }
  override getVisualDiffType(): DiffType {
    return this.metadata.visualDiffType;
  }
  override hasURLFields() {
    return Boolean(this.metadata.urlFieldValues?.length);
  }
  override getCSS(options?: GetCssClassNamesOption) {
    const { cssClassNames } = this.metadata;
    return options?.useAsBackgroundStyle
      ? classes(cssClassNames, LI_BACKGROUND_CLASS_NAME)
      : cssClassNames;
  }
  override getItemLabels(): ItemLabels | undefined {
    return this.metadata.itemLabels;
  }
  override setItemLabels(itemLabels: ItemLabels): void {
    this.metadata.itemLabels = itemLabels;
  }

  getUrlFieldValues() {
    return this.metadata.urlFieldValues;
  }
  setUrlFieldValues(urlFieldValues: URLFieldValue[] | null) {
    this.metadata.urlFieldValues = urlFieldValues;
  }

  getConditionalFormattingState(): ConditionalFormattingState {
    return this.metadata.conditionalFormattingState;
  }

  setConditionalFormattingState(
    conditionalFormattingState: ConditionalFormattingState
  ): void {
    this.metadata.conditionalFormattingState = conditionalFormattingState;
  }

  getRepresentationData() {
    return this.metadata.representationData;
  }
  setRepresentationData(representationData: RepresentationData | null) {
    this.metadata.representationData = representationData;
  }

  getImage() {
    const representationData = this.metadata.representationData;

    if (!representationData || !representationData.isImage) {
      return '';
    }

    return this.metadata.representationData?.value ?? '';
  }

  getShape() {
    return this.metadata.representationData?.shapeName || 'predefinedProcess';
  }

  getCssColorAttributes() {
    return this.metadata.color;
  }
  setCssColorAttributes(color: SVGColorAttributes) {
    this.metadata.color = color;
  }

  getIsContext() {
    return this.metadata.isContext;
  }
  setIsContext(isContext: boolean) {
    this.metadata.isContext = isContext;
  }

  protected getRawHash() {
    return `${super.getRawHash()}${this.subLabel}${this.getRepresentationData()?.value ?? ''}${Number(this.collapsed)}`;
  }
}
